// Author byline for blog posts (old arkusinc.policy "by <author>" line + "More by" box).
// Author data lives in Plone as published pages /author/<userid> (title = full name,
// description = bio line, image = portrait), created from the old member data.
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getContent } from '@plone/volto/actions/content/content';
import FormattedDate from '@plone/volto/components/theme/FormattedDate/FormattedDate';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

export const useAuthor = (userid) => {
  const key = `arkus-author-${userid}`;
  const dispatch = useDispatch();
  const sub = useSelector((state) => state.content?.subrequests?.[key]);
  useEffect(() => {
    if (userid && !sub?.loaded && !sub?.loading && !sub?.error) {
      dispatch(getContent(`/author/${userid}`, null, key));
    }
  }, [userid, key, dispatch, sub?.loaded, sub?.loading, sub?.error]);
  return sub?.data;
};

const portraitURL = (author) =>
  author?.image ? `${flattenToAppURL(author['@id'])}/@@images/image` : null;

export const AuthorByline = ({ content }) => {
  const userid = content?.creators?.[0];
  const author = useAuthor(userid);
  const date = content?.effective || content?.created;
  const url = author ? flattenToAppURL(author['@id']) : null;
  const img = portraitURL(author);
  return (
    <div className="arkus-byline">
      {img && (
        <Link to={url} className="arkus-byline-portrait" tabIndex={-1} aria-hidden="true">
          <img src={img} alt="" width="75" height="100" loading="lazy" />
        </Link>
      )}
      <div className="arkus-byline-text">
        <div className="arkus-byline-line">
          {date && (
            <FormattedDate
              date={date}
              format={{ year: 'numeric', month: 'long', day: 'numeric' }}
              className="arkus-byline-date"
            />
          )}
          {author && (
            <>
              {' '}
              <span className="arkus-byline-by">by</span>{' '}
              <Link to={url} className="arkus-byline-name">
                {author.title}
              </Link>
            </>
          )}
        </div>
        {author?.description && (
          <div className="arkus-byline-bio">{author.description}</div>
        )}
      </div>
    </div>
  );
};

export const AuthorBox = ({ content }) => {
  const userid = content?.creators?.[0];
  const author = useAuthor(userid);
  const subjects = (content?.subjects || []).filter((s) => s !== 'Blog');
  if (!author && !subjects.length) return null;
  const url = author ? flattenToAppURL(author['@id']) : null;
  const img = portraitURL(author);
  return (
    <aside className="arkus-authorbox">
      {author && (
        <div className="arkus-authorbox-author">
          {img && (
            <Link to={url} tabIndex={-1} aria-hidden="true">
              <img src={img} alt="" width="75" height="100" loading="lazy" />
            </Link>
          )}
          <div>
            <div className="arkus-authorbox-label">About the author</div>
            <Link to={url} className="arkus-authorbox-name">
              {author.title}
            </Link>
            {author.description && (
              <p className="arkus-authorbox-bio">{author.description}</p>
            )}
            <Link to={url} className="arkus-authorbox-more">
              More by {author.title} →
            </Link>
          </div>
        </div>
      )}
      {subjects.length > 0 && (
        <div className="arkus-authorbox-tags">
          <div className="arkus-authorbox-label">Filed under</div>
          {subjects.map((s, i) => (
            <React.Fragment key={s}>
              {i > 0 && ', '}
              <Link to={`/search?Subject=${encodeURIComponent(s)}`}>{s}</Link>
            </React.Fragment>
          ))}
        </div>
      )}
    </aside>
  );
};
